import React, { useState } from 'react';
import {
  type ColumnDef,
  flexRender,
  type ReactTable,
} from '@tanstack/react-table';
import type { AppTableFeatures } from './tableFeatures';

import { DebouncedInput } from './TableFilter';
import { FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import NestedMobileList from './NestedMobileList';

export default function MobileList<
  T extends { [key: string]: any },
  TChild extends { [key: string]: any } = T,
>({
  table,
  // createChildTable,
  subRowProp,
  // columnResizeMode,
  // showFilters = false,
  // alignCells = 'left',
  // allowWordWrap = false,
  // noEmptyRows = false,
  showPagination = true,
  // showFooter = false,
  // striped = true,
  columns2,
}: {
  table: ReactTable<AppTableFeatures, T>;
  createChildTable?: (data: any) => React.ReactNode;
  subRowProp?: keyof T;
  columnResizeMode: string;
  showFilters?: boolean;
  alignCells?: string;
  allowWordWrap?: boolean;
  noEmptyRows?: boolean;
  showPagination?: boolean;
  showFooter?: boolean;
  striped?: boolean;
  columns2?: ColumnDef<AppTableFeatures, TChild>[];
}) {
  const [isOpen, setIsOpen] = useState(false);

  // don't show filter or sorting if only one row
  // make exceptions for showing headers and cells for columns with id = expander or action
  return (
    <div className='w-full overflow-x-hidden'>
      <div className='relative w-full'>
        {table.getCoreRowModel().rows.length > 1 && (
          <div className='mb-4 p-1'>
            <DebouncedInput
              type='search'
              aria-label='Search all columns'
              value={table.state.globalFilter ?? ''}
              onChange={(value) => table.setGlobalFilter(String(value))}
              placeholder='Search all columns...'
            />
          </div>
        )}
        {table.getCoreRowModel().rows.length > 1 && (
          <div className='p-1'>
            <div
              id='accordion-open'
              data-accordion='open'
              className='my-1 mb-4'
            >
              <h2 id='accordion-open-heading-1' className='m-0'>
                <button
                  type='button'
                  className={`flex w-full items-center justify-between gap-3  border ${
                    isOpen
                      ? 'rounded-b-none rounded-t-lg border-b-0'
                      : 'rounded-lg'
                  } g-gray-50  block w-full  rounded-lg border border-gray-300 p-2.5
              text-sm text-gray-900 focus:border-gray-400 focus:ring-gray-400
               dark:border-0 dark:bg-gray-700 dark:text-gray-400 dark:placeholder-gray-400 dark:focus:ring-gray-400`}
                  data-accordion-target='#accordion-open-body-1'
                  aria-expanded={isOpen}
                  aria-controls='accordion-open-body-1'
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <span className='flex items-center'>Sorting</span>
                  <svg
                    data-accordion-icon
                    className='h-3 w-3 shrink-0 rotate-180'
                    aria-hidden='true'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 10 6'
                  >
                    <path
                      stroke='currentColor'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M9 5 5 1 1 5'
                    />
                  </svg>
                </button>
              </h2>
              <div
                id='accordion-open-body-1'
                className={`${isOpen ? 'block' : 'hidden'}`}
                aria-labelledby='accordion-open-heading-1'
              >
                <div className='rounded-b-lg border border-gray-200 p-4 dark:border-gray-700 dark:bg-gray-900'>
                  <div className='flex flex-wrap justify-between gap-2 px-1'>
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) =>
                        header.isPlaceholder ||
                        header.column.id === 'expander' ||
                        header.column.id === 'action' ? null : (
                          <button
                            key={header.id}
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none flex items-center justify-center border dark:border-0 rounded-md p-2 text-sm w-28 dark:bg-gray-800'
                                : 'flex items-center justify-center',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {(header.column.getCanSort() &&
                              {
                                asc: (
                                  <FaSortUp
                                    style={{ marginLeft: '.5rem' }}
                                  ></FaSortUp>
                                ),
                                desc: (
                                  <FaSortDown
                                    style={{ marginLeft: '.5rem' }}
                                  ></FaSortDown>
                                ),
                                false: (
                                  <FaSort
                                    style={{ marginLeft: '.5rem' }}
                                  ></FaSort>
                                ),
                              }[header.column.getIsSorted() as string]) ??
                              null}
                          </button>
                        ),
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <ul className='p-1'>
          {table.getRowModel().rows.map((row, i) => {
            return (
              <React.Fragment key={i}>
                <li className='mb-4'>
                  <div
                    key={row.id}
                    className='block w-full rounded-lg border border-gray-200
                     bg-white p-6 shadow hover:bg-gray-100
                       dark:border-0 dark:bg-gray-800
                       dark:hover:bg-gray-700'
                  >
                    <div className='flex flex-row flex-wrap items-center'>
                      {row.getVisibleCells().map((cell) => {
                        if (
                          !cell.getValue() &&
                          cell.column.id !== 'expander' &&
                          cell.column.id !== 'action' &&
                          cell.column.id !== 'select'
                        ) {
                          return '';
                        }
                        return (
                          <span
                            key={cell.id}
                            {...{
                              style: {
                                overflow: 'hidden',
                                padding: '5px 7px',
                              },
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </li>
                {subRowProp && columns2 && row.getIsExpanded() && (
                  <NestedMobileList
                    showPagination={row.original[subRowProp!].length > 9}
                    showFilters
                    data={row.original[subRowProp!]}
                    columns1={columns2}
                  />
                )}
              </React.Fragment>
            );
          })}
        </ul>
      </div>

      {showPagination && table.getCoreRowModel().rows.length > 0 && (
        <nav
          className='flex-column flex flex-wrap items-center justify-center gap-4 pt-4 sm:flex-row'
          aria-label='Table navigation'
        >
          <ul className=' flex h-8 text-sm '>
            <li>
              <button
                className='ms-0 flex h-8 
                items-center justify-center rounded-s-lg border border-gray-300 bg-white
                px-3 leading-tight  hover:bg-gray-100
                 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800 
                  dark:hover:bg-gray-700 dark:hover:text-white'
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </button>
            </li>

            <li>
              <div
                aria-current='page'
                className='ark:border-gray-700 flex h-8 
                w-[95px] items-center justify-center
                border border-gray-300 px-3 dark:border-gray-700 dark:bg-gray-800 '
              >
                <span className='mr-2 font-bold'>
                  {table.state.pagination.pageIndex + 1}
                </span>
                of <span className='ml-2'>{table.getPageCount()}</span>
              </div>
            </li>
            <li>
              <select
                className='m-0 block w-full h-8 border border-gray-300 
              py-[5px] pr-6 text-sm shadow-sm focus:border-indigo-300 focus:ring
              focus:ring-indigo-200 focus:ring-opacity-0 dark:border-gray-700 dark:bg-gray-800 '
                aria-label='rows per page'
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
                value={table.state.pagination.pageSize}
              >
                <option value='5'>5 rows</option>
                <option value='10'>10 rows</option>
                <option value='20'>20 rows</option>
                <option value='25'>25 rows</option>
                <option value='50'>50 rows</option>
                <option value='100'>100 rows</option>
              </select>
            </li>

            <li>
              <button
                className='flex h-8 w-[79px] items-center justify-center 
                rounded-e-lg border border-gray-300 bg-white px-3 leading-tight
                  hover:bg-gray-100 hover:text-gray-700 
                 disabled:cursor-not-allowed disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800
                   dark:hover:bg-gray-700 dark:hover:text-white'
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </button>
            </li>
          </ul>

          <div className='text-sm font-semibold'>
            {table.getFilteredRowModel().flatRows.length ?? 'N/A'} results
          </div>
        </nav>
      )}
    </div>
  );
}
